import { useCallback } from 'react';
import type { EventoDaColeta } from '../../api/coletas.js';
import { consultarHistoricoDaColeta } from '../../api/coletas.js';
import { useConsulta } from '../../api/useConsulta.js';
import { formatarDataHora, formatarHora } from '../../componentes/datas.js';
import { rotuloDaFilial } from '../../componentes/deposito.js';
import { PainelLateral } from '../../componentes/PainelLateral.js';
import { Resultado } from '../../componentes/Resultado.js';
import './BacklogDoPedido.css';

type Props = {
  readonly id: string;
  /** Já conhecido pela linha da tabela: o título aparece antes da resposta. */
  readonly pedido: string;
  readonly aoFechar: () => void;
};

/**
 * A partir de quanto a diferença entre os dois relógios vira informação.
 *
 * Abaixo disso é latência de rede e não interessa a ninguém. Acima, é a
 * resposta para "por que este pedido só apareceu no painel de noite?" — a
 * operação ficou represada na fila do coletor, provavelmente sem sinal.
 */
const ATRASO_QUE_IMPORTA_MS = 60_000;

type Gravidade = 'informacao' | 'sucesso' | 'atencao' | 'erro';

/**
 * A redação de cada acontecimento.
 *
 * Fica aqui, e não no middleware, pelo mesmo motivo dos chips de status: a API
 * devolve o que ACONTECEU, a tela decide como se diz. Ação que a tela ainda não
 * conhece aparece com o nome técnico da trilha em vez de sumir — o backlog é
 * append-only, e engolir um evento desconhecido esconderia justamente o que
 * ninguém previu.
 */
function tituloDoEvento(evento: EventoDaColeta): string {
  if (evento.acao === 'documento.aberto') {
    return 'Pedido aberto';
  }

  if (evento.acao !== 'coleta.registrada') {
    return evento.acao;
  }

  if (evento.naoEncontrado) {
    return 'Item não encontrado';
  }

  return evento.divergente ? 'Item coletado com divergência' : 'Item coletado';
}

function gravidadeDoEvento(evento: EventoDaColeta): Gravidade {
  if (evento.naoEncontrado) {
    return 'erro';
  }

  if (evento.divergente || evento.vinculoManual || evento.statusDaOperacao === 'Retida') {
    return 'atencao';
  }

  return evento.acao === 'coleta.registrada' ? 'sucesso' : 'informacao';
}

/** Quantos minutos a operação esperou na fila do coletor antes de chegar. */
function atrasoEmMinutos(evento: EventoDaColeta): number | null {
  if (evento.aconteceuEm === null) {
    return null;
  }

  const atraso = new Date(evento.registradoEm).getTime() - new Date(evento.aconteceuEm).getTime();

  return atraso >= ATRASO_QUE_IMPORTA_MS ? Math.round(atraso / 60000) : null;
}

/**
 * Filial · depósito de coleta → depósito de entrega. A filial aparece uma vez
 * só: os dois depósitos são da mesma, a filial de trabalho do operador.
 */
function ondeDe(evento: EventoDaColeta): string | null {
  if (evento.depositoColeta === null && evento.depositoEntrega === null) {
    return null;
  }

  const caminho = `${evento.depositoColeta ?? '—'} → ${evento.depositoEntrega ?? '—'}`;
  return evento.codFil === null ? caminho : `${rotuloDaFilial(evento.codFil)} · ${caminho}`;
}

function quantidadeDe(evento: EventoDaColeta): string | null {
  if (evento.quantidadeColetada === null) {
    return null;
  }

  const coletada = Number(evento.quantidadeColetada);
  const solicitada = Number(evento.quantidadeSolicitada ?? evento.quantidadeColetada);

  return `${String(coletada)} de ${String(solicitada)}`;
}

/**
 * O backlog de um pedido (UC10) — o que aconteceu, quando, e por quem.
 *
 * A ordem é a da história: a abertura do documento na frente, o resto em ordem
 * cronológica pelo horário do APARELHO, que é quando a peça saiu da prateleira.
 * O horário do servidor só aparece quando a distância entre os dois explica
 * alguma coisa — coleta feita em modo avião que só chegou horas depois.
 */
export function BacklogDoPedido({ id, pedido, aoFechar }: Props) {
  const buscar = useCallback(() => consultarHistoricoDaColeta(id), [id]);
  const consulta = useConsulta(buscar);

  return (
    <PainelLateral
      titulo={pedido}
      subtitulo="Tudo que aconteceu dentro deste pedido"
      aoFechar={aoFechar}
    >
      <Resultado
        consulta={consulta}
        espera="Carregando o histórico…"
        estaVazio={(dados) => dados.eventos.length === 0}
        vazio={<p className="backlog__vazio">Este pedido ainda não tem histórico registrado.</p>}
      >
        {(historico) => (
          <>
            <p className="backlog__resumo">
              {historico.itensConcluidos} de {historico.totalItens} itens · aberto em{' '}
              {formatarDataHora(historico.criadoEm)}
            </p>

            <ol className="backlog__linha-do-tempo">
              {historico.eventos.map((evento) => (
                <li
                  key={evento.id}
                  className={`backlog__evento backlog__evento--${gravidadeDoEvento(evento)}`}
                >
                  <span className="backlog__hora mono">
                    {formatarHora(evento.aconteceuEm ?? evento.registradoEm)}
                  </span>

                  <span className="backlog__corpo">
                    <span className="backlog__titulo">{tituloDoEvento(evento)}</span>

                    {evento.sku !== null && (
                      <span className="backlog__detalhe mono">
                        {evento.sku}
                        {evento.linha === null ? '' : ` · linha ${String(evento.linha)}`}
                        {quantidadeDe(evento) === null ? '' : ` · ${quantidadeDe(evento) ?? ''}`}
                        {ondeDe(evento) === null ? '' : ` · ${ondeDe(evento) ?? ''}`}
                      </span>
                    )}

                    {evento.vinculoManual && (
                      <span className="backlog__aviso">
                        Vínculo manual
                        {evento.codigoLido === null ? '' : ` · etiqueta lida: ${evento.codigoLido}`}
                      </span>
                    )}

                    {evento.statusDaOperacao === 'Retida' && (
                      <span className="backlog__aviso">Retida — aguarda decisão no painel</span>
                    )}

                    <span className="backlog__autor">
                      {evento.autor ?? 'automático'} · {evento.origem}
                      {atrasoEmMinutos(evento) === null
                        ? ''
                        : ` · chegou ao servidor ${String(atrasoEmMinutos(evento))} min depois`}
                    </span>
                  </span>
                </li>
              ))}
            </ol>
          </>
        )}
      </Resultado>
    </PainelLateral>
  );
}
